/**
 * caseReport.js — s98 Phases 4 + 6 + 7 (D468): the Case Report and its two ways out.
 *
 * ★★★ ONE OWNER for the share hub's build, the PDF fetch, the doctor directory's specialty
 *   chips and the fax status steps. ShareHubScreen, FindDoctorScreen and SendReportScreen
 *   import from here; none keeps a copy.
 * ★★★ ONE PDF: the server's (GET /cases/<id>/report.pdf), his ruling 2026-10-07 #1. View PDF,
 *   save, share and the fax all carry the same document. The phone-side generator
 *   (visitPrepPDF.js) is not used on this path.
 */
import { casesAPI, aiAPI } from '../services/api'
import { saveOrShareBlob } from './saveOrShareBlob'
import { resolveSpecialtyId } from './specialtyResolver'
import { PRIMARY_CARE_TAXONOMY } from './specialtyChoices' // s101: one copy

/** The build's steps, in order. 'profile' is checked before the build starts (missingBeforeShare). */
export const BUILD_STEPS = ['profile', 'symptoms', 'photos', 'summary', 'code']

/** "Oct 16" for the code's expiry, Los Angeles time, as DiagnoseScreen formats it. */
export function formatShareExpiry(expiresAt) {
  const d = expiresAt ? new Date(expiresAt) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
}

/**
 * Build the Case Report for a case: the share code (minted once; an existing code is kept),
 * then the clinician hand-off the PDF prints. `onStep(step)` reports progress for the chips.
 * Returns { code, expiration }. Throws when the code cannot be minted (the hand-off is
 * best-effort: the PDF prints its own fallback without it).
 */
export async function buildCaseReport(c, updateAndSaveCase, onStep = () => {}) {
  onStep('summary')
  const handoff = await aiAPI.generateHandoffSummary(c.id)
  if (handoff) {
    try { await casesAPI.update(c.id, { preVisitSummary: handoff }) } catch (e) { /* the store save below retries it */ }
  }
  onStep('code')
  let code = c.shareCode || null
  let expiration = c.shareExpiration || ''
  if (!code) {
    const r = await casesAPI.generateShareCode(c.id, false, null, null, 'external')
    if (!r || !r.shareCode) throw new Error('share code not minted')
    code = r.shareCode
    expiration = formatShareExpiry(r.expiresAt)
  }
  await updateAndSaveCase({
    shareCode: code, shareExpiration: expiration, isShared: true, status: 'shared',
    doctorSource: c.doctorSource || 'external', doctor_source: c.doctorSource || 'external',
    ...(handoff ? { preVisitSummary: handoff } : {}),
  })
  return { code, expiration }
}

/** Fetch the server's Case Report PDF as a Blob (the in-app viewer, s99 D477). err.status on a refusal. */
export async function fetchCaseReportPdf(c) {
  const res = await casesAPI.reportPdf(c.id)
  if (!res || !res.ok) {
    const err = new Error('report pdf ' + (res ? res.status : 'network'))
    err.status = res ? res.status : 0
    throw err
  }
  return res.blob()
}

/** Fetch the server's Case Report PDF and hand it to the phone's share sheet (web: download). */
export async function shareCaseReportPdf(c, { title } = {}) {
  const blob = await fetchCaseReportPdf(c)
  const num = String(c.caseNumber || c.case_number || String(c.id).slice(0, 8)).replace(/^#/, '')
  await saveOrShareBlob(blob, `orella-case-report-${num}.pdf`, { title: title || 'Case Report' })
}

// NUCC taxonomy prefixes per complaint area (the directory's scope, his ruling 2026-10-07 #2:
//   MD/DO 207*/208*, NP 363L, PA 363A, dentists 122, podiatrists 213E).
// ★ PROD-1188: skin, stomach-gut, heart, infection, mental-health and teeth send the same exact codes as
//   SPECIALTY_TAXONOMY in utils/specialtyChoices.js (reasons there; the gate holds them equal to the API's).
const AREA_TAXONOMY = {
  ear: ['207Y'], nose: ['207Y'], throat: ['207Y'], 'head-neck': ['207Y'],
  eye: ['207W'], 'stomach-gut': ['207RG0100X'], 'muscle-joints': ['207X', '207RR0500X'], skin: ['207N00000X', '207ND0099X', '207NI0002X', '207NP0225X', '207NS0135X'],
  pain: ['208VP', '207LP2900X', '2081P2900X', '2084P2900X'], urinary: ['2088'], heart: ['207RC0000X', '207RC0001X', '207RI0011X', '207RA0001X', '207RA0002X'], lungs: ['207RP1001X'], kidney: ['207RN0300X'],
  blood: ['207RH'], 'womens-health': ['207V'], 'brain-nerves': ['2084N'], 'mental-health': ['2084P0800X', '2084P0802X', '2084P0804X', '2084P0805X', '2084P0015X'],
  vascular: ['2086S0129X'], infection: ['207RI0200X'], sleep: ['207RS0012X', '2084S0012X', '207QS1201X', '207YS0012X', '2083S0010X'], feet: ['213E'], teeth: ['1223'],
  'plastic-surgery': ['2082'], anesthesiology: ['207L'],
}
// s101: one copy, in utils/specialtyChoices.js (the picker's choices use it too).
export { PRIMARY_CARE_TAXONOMY }
// Teeth and feet have their own doctors; primary care is not offered as a chip for them.
const NO_PRIMARY = new Set(['teeth', 'feet'])

/**
 * The finder's chips for a case: [{ id, prefixes, areaId? }]. The first chip is the case's own
 * specialty (when it has one), then primary care, then 'all' (no taxonomy filter).
 */
export function specialtyChips(c) {
  const areaId = c ? resolveSpecialtyId(c.specialtyId || c.specialty?.id || c.specialtyName) : null
  const chips = []
  if (areaId && AREA_TAXONOMY[areaId]) chips.push({ id: 'area', areaId, prefixes: AREA_TAXONOMY[areaId] })
  if (!areaId || !NO_PRIMARY.has(areaId)) chips.push({ id: 'primary', prefixes: PRIMARY_CARE_TAXONOMY })
  chips.push({ id: 'all', prefixes: [] })
  return chips
}

/** A typed search: five digits = a ZIP, anything else = a name (with the patient's ZIP for distance). */
export function searchParams(text, homeZip, prefixes) {
  const s = String(text || '').trim()
  const zip5 = (z) => (String(z || '').match(/\d{5}/) || [''])[0]
  const p = {}
  if (/^\d{5}(-\d{4})?$/.test(s)) p.zip = s.slice(0, 5)
  else {
    if (s) p.q = s
    if (zip5(homeZip)) p.zip = zip5(homeZip)
  }
  if (prefixes && prefixes.length) p.taxonomy = prefixes.join(',')
  return p
}

/** '(415) 555-0177' from any US form; '' when it is not ten digits. */
export function formatUsPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '')
  if (d.length === 11 && d[0] === '1') d = d.slice(1)
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : ''
}

/** The fax steps the Sent screen shows, and which one is current. */
export const FAX_STEPS = ['queued', 'sending', 'delivered']
export function faxStepIndex(status) {
  if (status === 'delivered') return 3
  if (status === 'sending') return 1
  return 0
}
export const isFaxFinal = (status) => status === 'delivered' || status === 'failed'
